import {
  BdgPrimaryRecipient,
  decideActiveBdgTier,
  isPrimaryTierUnavailable,
} from '@/modules/entityDiagram/bdgActiveTier';

interface RootBdgState {
  triggeringClientIds: string[];
  isSurvivorship: boolean;
}

export interface PickActiveBdgFormTierArgs {
  isEditingRootTarget: boolean;
  primaryRecipientsForTierCheck: BdgPrimaryRecipient[];
  possiblePrimaryClientIds: string[];
  firstGrantorDeathId: string | null;
  /**
   * BDG metadata published by the parent diagram. Null when the parent
   * diagram isn't BDG-aware (e.g., a non-root BDG form opened from a Rev
   * Trust DP diagram). In that case the form is rendering a downstream
   * BDG entity, so the tier check treats both primary clients as dead.
   */
  bdg: RootBdgState | null;
}

/**
 * Picks the active BDG tier for the form's prefetch. Mirrors
 * `resolveEntityNormalizedDispositions` so the diagram and the form stay
 * in lock-step. Two branches:
 *
 * - Root BDG: replays `decideActiveBdgTier` with the published triggering
 *   list and the current death-order selection.
 * - Downstream BDG: cascade displays this entity at the parent's
 *   second-death slot, so both primaries are presumed dead — the tier is
 *   contingent iff the primary tier is unavailable under that
 *   deceased-set.
 */
export function pickActiveBdgFormTier({
  isEditingRootTarget,
  primaryRecipientsForTierCheck,
  possiblePrimaryClientIds,
  firstGrantorDeathId,
  bdg,
}: PickActiveBdgFormTierArgs): { useContingent: boolean } {
  if (!isEditingRootTarget) {
    const useContingent = isPrimaryTierUnavailable(
      primaryRecipientsForTierCheck,
      new Set(possiblePrimaryClientIds),
      null
    );
    return { useContingent };
  }
  if (!bdg || !firstGrantorDeathId) return { useContingent: false };
  const { useContingent } = decideActiveBdgTier({
    primaryRecipientsForTierCheck,
    selectedPrimaryClientId: firstGrantorDeathId,
    triggeringClientIds: bdg.triggeringClientIds,
    isSurvivorship: bdg.isSurvivorship,
    possiblePrimaryClientIds,
  });
  return { useContingent };
}
